import type {
  DeliverableDto,
  DeliverablesResponse,
  HomeResponse,
  OrderLinksDto,
  SalespageDeliveryDto,
  SectionState
} from "../shared/dto";

// The browser-side FALLBACK twins of the server's sales derivation
// (worker/src/sales-steps.ts), used only for cached payloads that carry no
// `salesSteps`, plus the two phase readers the sales page step shares with the
// rail. Split out of `rail-model.ts` (F171 growth tripwire); `rail-model.ts`
// re-exports the public ones, so no import site changed.

export function newest(delivery: DeliverablesResponse | null | undefined, kind: DeliverableDto["kind"]): DeliverableDto | null {
  return delivery?.deliverables
    .filter((item) => item.kind === kind)
    .sort((left, right) => right.version - left.version)[0] ?? null;
}

export function deliverableState(
  delivery: DeliverablesResponse | null | undefined,
  kind: DeliverableDto["kind"],
  fallback: SectionState
): SectionState {
  const item = newest(delivery, kind);
  if (!item) return fallback === "done" ? "next" : fallback;
  if (item.status === "approved") return "done";
  return item.status === "review" ? "next" : "working";
}

export function gatedDeliverableState(
  unlocked: boolean,
  delivery: DeliverablesResponse | null | undefined,
  kind: DeliverableDto["kind"],
  fallback: SectionState
): SectionState {
  if (!unlocked) return "locked";
  return newest(delivery, kind) ? deliverableState(delivery, kind, fallback) : "next";
}

/**
 * F120: the take-away phase of the sales page step is open. Twin of the
 * server's `takeawayUnlocked` (worker/src/sales-steps.ts) expressed in the
 * facts a browser payload carries: the integration milestone, the historic
 * stripe unlock, or the server's own lock on the delivery menu (a pre-F82
 * cached payload has none of the order-links fields and speaks only through
 * `locked`). ONE derivation, read by the rail AND by the step's phase 3
 * (src/sections/OrderLinks.tsx), so the rail can never disagree with the
 * phase the client is looking at.
 */
export function salespageTakeawayReached(
  orderLinks: OrderLinksDto | null | undefined,
  salespage: SalespageDeliveryDto | null | undefined
): boolean {
  if (orderLinks?.integratedAt || orderLinks?.legacyStripeUnlocked) return true;
  return Boolean(salespage && !salespage.locked);
}

/**
 * F137: the publication FACT of the sales page, as the browser reads it. The
 * server records it on the first take-away act (download or Netlify publish)
 * and ships it as `salespage.published`; a client who published through us
 * before that milestone existed is recognised by their own Netlify site, so
 * nothing locks back.
 *
 * 🔴 This is the completion of phase 3, never its unlock -
 * `salespageTakeawayReached` above is the unlock and the two must not be
 * confused again (F120 read the unlock as the completion, which is what put a
 * green check over a phase nobody had done).
 */
export function salespagePublished(
  salespage: SalespageDeliveryDto | null | undefined
): boolean {
  return Boolean(salespage?.published) || Boolean(salespage?.netlify);
}

/**
 * F120 fallback twin of the server's sales page step (worker/src/sales-steps.ts):
 * the step is done only when all THREE phases are - approval, order links
 * integrated, publication DONE (F137: the recorded act, not the open phase).
 * Only reached for cached payloads that carry no salesSteps; the server graph
 * stays the truth.
 */
export function salespageStepState(
  strategyApproved: boolean,
  delivery: DeliverablesResponse | null | undefined,
  fallback: SectionState
): SectionState {
  const state = gatedDeliverableState(strategyApproved, delivery, "salespage", fallback);
  if (state !== "done") return state;
  const reached = salespageTakeawayReached(delivery?.salespage?.orderLinks, delivery?.salespage);
  return reached && salespagePublished(delivery?.salespage) ? "done" : "next";
}

/**
 * F101 fallback twin of the server's handover gate (worker/src/sales-steps.ts):
 * all four client materials approved. Only reached for cached payloads that
 * carry no salesSteps; the server graph stays the truth.
 */
export function materialsApproved(
  delivery: DeliverablesResponse | null | undefined,
  strategyApproved: boolean,
  pageApproved: boolean
): boolean {
  return strategyApproved
    && pageApproved
    && newest(delivery, "emails")?.status === "approved"
    && newest(delivery, "webinar")?.status === "approved";
}

// F137: a publication is a publication whichever act recorded it, so the
// legacy twin stopped asking only about Netlify. (Reached only for cached
// payloads without salesSteps; the F82 journey has no separate publication
// step, and what still reads this is the app-release fallback below.)
export function publicationState(
  delivery: DeliverablesResponse | null | undefined,
  pageApproved: boolean,
  stripeState: SectionState
): SectionState {
  if (salespagePublished(delivery?.salespage)) return "done";
  return pageApproved && stripeState === "done" ? "next" : "locked";
}

export function appQuestionnaireState(home: HomeResponse): SectionState {
  if (!home.client.includesApp) return "done";
  if (home.appWizard?.submitted) return "done";
  if ((home.appWizard?.progress.done ?? 0) > 0) return "working";
  const app = home.sections.find((item) => item.key === "app")?.state ?? "locked";
  return app === "working" || app === "done" ? "next" : "locked";
}

export function appReleaseState(home: HomeResponse, publication: SectionState): SectionState {
  if (!home.client.includesApp) return "done";
  if (publication !== "done") return "locked";
  const app = home.sections.find((item) => item.key === "app")?.state ?? "locked";
  return app === "done" ? "done" : app === "working" ? "working" : "locked";
}
